import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, MapPin, Star, ShieldCheck } from "lucide-react";
import HeroSearchBar from "@/components/search/HeroSearchBar";

/**
 * HeroVibe — the site's primary homepage hero.
 *
 * Full-bleed spa background image + glass-morphism search card. Uses a
 * `<picture>` element so modern browsers get a 72 KB WebP while older ones
 * fall back to a 151 KB progressive JPEG. Both are served from `/public/`
 * to avoid third-party CDN dependencies.
 *
 * The gradient overlay is dark on the left (protecting the headline copy)
 * and fades to nearly transparent on the right (keeping the model's face
 * and the bokeh candles visible). Gold accent matches the warm bokeh glow.
 */
export default function HeroVibe() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden isolate"
    >
      {/* Background image — decorative, hidden from AT */}
      <picture>
        <source srcSet="/hero-spa.webp" type="image/webp" />
        <img
          src="/hero-spa.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
      </picture>

      {/* Dark→transparent gradient for readable copy on the left */}
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-tr from-[rgba(24,16,8,0.68)] via-[rgba(24,16,8,0.42)] to-[rgba(24,16,8,0.10)]"
        aria-hidden="true"
      />
      {/* Subtle grain overlay for premium texture */}
      <div className="absolute inset-0 -z-10 noise-overlay opacity-40 mix-blend-overlay" aria-hidden="true" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20 sm:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-6 text-white">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full backdrop-blur-md border bg-[rgba(255,255,255,0.14)] border-[rgba(237,201,138,0.45)] text-[#EDC98A] text-xs mb-5"
          >
            <Sparkles size={14} /> Pune &amp; Pimpri-Chinchwad&apos;s Premier Wellness Directory
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.35)]">
            Find The <span className="text-[#EDC98A]">Best Spa</span> Near You in Pune &amp; Pimpri-Chinchwad
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/90 leading-relaxed max-w-xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.35)]">
            Explore verified spas, wellness centers, and massage destinations across Pune &amp; Pimpri-Chinchwad. Curated listings, real reviews, instant contact.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6 text-white/95">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck size={18} className="text-[#EDC98A]" />
              <span><strong>Verified</strong> Listings</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Star size={18} className="text-[#EDC98A] fill-[#EDC98A]" />
              <span><strong>Real</strong> Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={18} className="text-[#EDC98A]" />
              <span><strong>40+</strong> Localities</span>
            </div>
          </div>
        </div>

        {/* RIGHT — glass search card */}
        <div className="lg:col-span-6">
          <div className="rounded-3xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-[0_24px_60px_rgba(0,0,0,0.25)] p-2 sm:p-3">
            <HeroSearchBar />
          </div>
          <div className="mt-4 text-xs text-center text-white/90 drop-shadow">
            Are you a spa owner?{" "}
            <Link
              to="/register"
              data-testid="hero-list-your-spa-link"
              className="underline font-medium text-[#EDC98A]"
            >
              List your spa free
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
